import {
  ActionsMenuAction,
  BaseTab,
  BaseTabPanel,
  BaseTabs,
  Button,
  colors,
  Modal,
  PageTitle,
  Stack,
  Table,
  TableContainer,
  TabPanelContainer,
  Typography,
  useTabState,
} from '@ui-components/neo.tax';
import { useEffect, useState, useMemo } from 'react';

import { useRdContext } from '@neo.tax/rd/context';
import { useRdBusinessComponentUploads } from '@neo.tax/rd/hooks';
import {
  useDisabledProviders,
  useTicketingConnectModal,
  CONNECT_SLIDE_OVER_ACTIONS,
  DataSourcesTableItem,
  FileUploadTicketingProvider,
  ProviderType,
  TicketingEntityType,
  useCurrentCompany,
  useCurrentUserScopeForCurrentCompany,
  useUserFromAuth,
  bulkPriorYearTaxDocumentToDataSourcesTableItem,
  bulkTicketingFileUploadToDataSourcesTableItem,
  dataSourcesTableItemMapper,
  githubEmailMappingUploadToDataSourcesTableItem,
  timesheetUploadToDataSourcesTableItem,
  dataSourceTableCellClassNames,
  syncedRdPayrollCostToDataSourcesTableItem,
  ConnectProvidersModal,
  DeleteFiscalYearProvider,
  DeleteTicketingFileUpload,
  GithubEmailMappingSection,
  GithubEmailMappingUploadModal,
  useGithubEmailMappingData,
  OneSchemaModal,
  OneSchemaTimesheetModal,
  TicketingConnections,
  TicketingFileUploadModal,
  DataSourceSection,
  accountingTokenToDataSourcesTableItem,
  AccountingAcquisitionMethod,
  payrollTokenToDataSourcesTableItem,
  rdBusinessComponentUploadToDataSourcesTableItem,
  RdBusinessComponentUploadFragment,
  RdBusinessComponentUploadStatus,
} from '@neo.tax/shared';

import { DeletePriorFiscalYearDocument } from '../delete-prior-fiscal-year-document';
import { DeleteRdBusinessComponentUpload } from '../delete-rd-business-component-upload';
import { PriorYearTaxDocumentsModal } from '../prior-year-tax-document-upload-modal';
import { RdBusinessComponentsUploadModal } from '../rd-business-components-upload-modal';
import { RdLockPageLayout } from '../rd-lock-page-layout';
import { useRdDataSourcesTableColumns, useRdDataSourceLayoutAnalyticIds } from './hooks';

const TAB_IDS = {
  accounting: 'data-sources-tab-accounting',
  businessComponents: 'data-sources-tab-business-components',
  payroll: 'data-sources-tab-payroll',
  tax: 'data-sources-tab-tax',
  ticketing: 'data-sources-tab-ticketing',
  timesheets: 'data-sources-tab-timesheets',
};

/**
 * Why a new business components CSV cannot be uploaded, or undefined when it can. A tax credit can hold
 * several lists, but only one file imports at a time.
 */
const getBusinessComponentsUploadBlockedReason = (uploads: RdBusinessComponentUploadFragment[]): string | undefined => {
  if (uploads.some((upload) => upload.status === RdBusinessComponentUploadStatus.Pending)) {
    return 'A business components file is still validating.';
  }
  return undefined;
};

export const RdDataSourcesLayout = (): React.ReactElement => {
  const { company } = useCurrentCompany();
  const { fiscalYear } = useRdContext();
  const {
    shouldPromptUpload: shouldPromptGithubEmailUpload,
    shouldShowUploadButton: shouldShowGithubEmailUploadButton,
    uploads: githubEmailMappingUploads,
  } = useGithubEmailMappingData({
    companyId: company?.id ?? '',
    syncedTicketingProviders: company?.syncedTicketingProviders ?? [],
  });
  const [isOpenGithubEmailMappingModal, setIsOpenGithubEmailMappingModal] = useState<boolean>(false);
  const {
    connectAccountingBtnId,
    connectPayrollBtnId,
    uploadBusinessComponentsBtnId,
    uploadTaxDocsBtnId,
    uploadTicketingProviderBtnId,
  } = useRdDataSourceLayoutAnalyticIds();
  const { uploads: businessComponentUploads } = useRdBusinessComponentUploads(fiscalYear.id);
  const businessComponentUploadItems = useMemo(
    () => businessComponentUploads.map(rdBusinessComponentUploadToDataSourcesTableItem),
    [businessComponentUploads],
  );
  const businessComponentsUploadBlockedReason = getBusinessComponentsUploadBlockedReason(businessComponentUploads);
  const [isOpenBusinessComponentsUploadModal, setIsOpenBusinessComponentsUploadModal] = useState<boolean>(false);

  const [isOpenTicketingFileUploadModal, setIsOpenTicketingFileUploadModal] = useState<boolean>(false);
  const [isOpenOneSchemaModal, setIsOpenOneSchemaModal] = useState<boolean>(false);
  const [isOpenOneSchemaTimesheetModal, setIsOpenOneSchemaTimesheetModal] = useState<boolean>(false);
  const [isOpenPriorYearTaxDocumentModal, setIsOpenPriorYearTaxDocumentModal] = useState<boolean>(false);
  const [oneSchemaParameters, setOneSchemaParameters] = useState<{
    oneSchemaJwt?: string;
    permanentUrl?: string;
    signedUrl?: string;
    ticketingEntityType?: TicketingEntityType;
    ticketingProvider?: FileUploadTicketingProvider;
  }>({});

  const tabState = useTabState({ defaultSelectedId: TAB_IDS.accounting });

  // Accounting and payroll sources hold financial data, so only users with financial access change them;
  // non-financial users may also manage business components, tax documents and ticketing. Sections they
  // cannot change render as a read-only list.
  const { isNonFinancial, limitAccessToFinancialData } = useCurrentUserScopeForCurrentCompany();
  const canModifyFinancialSources = !limitAccessToFinancialData;
  const canModifyTaxAndTicketingSources = canModifyFinancialSources || isNonFinancial;

  const {
    close: closeConnectProvidersModal,
    connectModalState,
    initialGitHubConnectMethod,
    initialOrganizationSlug,
    initialProvider,
    openForConnectionAction,
    openForTypes,
  } = useTicketingConnectModal();

  // A ticketing connect session belongs to the Ticketing tab — including one reopened by a GitHub App
  // install return, which lands on this page with the tab state at its default. That return reopens
  // the slide-over from the URL alone, so the permission gates the open state, not just the buttons.
  const offersFinancialProviders = Array.from(connectModalState.showProvidersInTypes).some(
    (providerType) => providerType !== ProviderType.Ticketing,
  );
  const isConnectOpen =
    connectModalState.isOpen &&
    (offersFinancialProviders ? canModifyFinancialSources : canModifyTaxAndTicketingSources);
  const isTicketingConnectOpen = isConnectOpen && connectModalState.showProvidersInTypes.has(ProviderType.Ticketing);
  const { setSelectedId } = tabState;
  useEffect(() => {
    if (isTicketingConnectOpen) {
      setSelectedId(TAB_IDS.ticketing);
    }
  }, [isTicketingConnectOpen, setSelectedId]);

  const disabledProviders = useDisabledProviders({
    disableConditions: { accounting: 'sync', payroll: 'sync' },
    fiscalYearId: fiscalYear.id,
  });

  const [itemToDelete, setItemToDelete] = useState<DataSourcesTableItem>();
  const connectionTableColumns = useRdDataSourcesTableColumns({
    canModify: canModifyFinancialSources,
    setItemToDelete,
  });
  const fileUploadTableColumns = useRdDataSourcesTableColumns({
    canModify: canModifyFinancialSources,
    setItemToDelete,
    tableType: 'file-upload',
  });
  const taxFileUploadTableColumns = useRdDataSourcesTableColumns({
    canModify: canModifyTaxAndTicketingSources,
    setItemToDelete,
    tableType: 'file-upload',
  });
  const payrollFileUploadTableColumns = useRdDataSourcesTableColumns({
    canModify: canModifyFinancialSources,
    setItemToDelete,
    tableType: 'payroll-file-upload',
  });
  const ticketingFileUploadTableColumns = useRdDataSourcesTableColumns({
    canModify: canModifyTaxAndTicketingSources,
    setItemToDelete,
    tableType: 'ticketing-file-upload',
  });
  const businessComponentsFileUploadTableColumns = useRdDataSourcesTableColumns({
    canModify: canModifyTaxAndTicketingSources,
    setItemToDelete,
    tableType: 'business-components-file-upload',
  });
  const { isSuperUserMode } = useUserFromAuth();

  const openAccountingModal = (): void => openForTypes([ProviderType.Accounting]);
  const openPayrollModal = (): void => openForTypes([ProviderType.Payroll]);
  const openTicketingModal = (): void => openForTypes([ProviderType.Ticketing]);

  const onCancelToggleModal = (): void => {
    setItemToDelete(undefined);
  };

  const tableData = useMemo(() => {
    if (!company) {
      return {
        accountingConnections: [],
        accountingUploads: [],
        payrollConnections: [],
        payrollUploads: [],
        taxUploads: [],
        ticketingUploads: [],
        timesheetUploads: [],
      };
    }

    // Get IDs of RD cost file sync events to filter them out from payroll providers
    const rdCostSyncEventIds = new Set(fiscalYear.syncedRdPayrollCost.map((rdCost) => rdCost.id));
    const liveAccountingTokens = (company.accountingTokens ?? []).filter(
      (token) => token.acquisitionMethod !== AccountingAcquisitionMethod.File,
    );
    const accountingProviders = fiscalYear.syncedProviders.accountingProviders;
    const accountingConnections: DataSourcesTableItem[] = liveAccountingTokens.flatMap((token) => {
      const syncedProviders = accountingProviders.filter(
        (provider) => provider.providerName === token.accountingProvider,
      );
      if (syncedProviders.length === 0) {
        return [accountingTokenToDataSourcesTableItem(token)];
      }
      return syncedProviders.map((provider) => ({
        ...dataSourcesTableItemMapper(provider),
        originalSyncDate: token.createdAt,
      }));
    });

    return {
      accountingConnections,
      accountingUploads: accountingProviders
        .filter((provider) => !liveAccountingTokens.some((token) => token.accountingProvider === provider.providerName))
        .map(dataSourcesTableItemMapper),
      // Filter out RD cost file sync events from payroll providers since they're shown separately
      payrollConnections: (company.payrollTokens ?? []).map(payrollTokenToDataSourcesTableItem),
      payrollUploads: [
        ...fiscalYear.syncedProviders.payrollProviders
          .filter((provider) => !rdCostSyncEventIds.has(provider.id))
          .map(dataSourcesTableItemMapper),
        ...fiscalYear.syncedRdPayrollCost.map(syncedRdPayrollCostToDataSourcesTableItem),
      ],
      ticketingUploads: [
        ...company.bulkTicketingDataUploads.map(bulkTicketingFileUploadToDataSourcesTableItem),
        ...githubEmailMappingUploads.map(githubEmailMappingUploadToDataSourcesTableItem),
      ],
      taxUploads: company.bulkPriorYearTaxDocumentUploads.map(bulkPriorYearTaxDocumentToDataSourcesTableItem),
      timesheetUploads: company.timesheetUploads.map(timesheetUploadToDataSourcesTableItem),
    };
  }, [fiscalYear, company, githubEmailMappingUploads]);

  if (!company) {
    return <></>;
  }

  const renderSources = (
    data: DataSourcesTableItem[],
    emptyMessage: string,
    columns = connectionTableColumns,
  ): React.ReactElement => (
    <TableContainer>
      <Table<DataSourcesTableItem>
        columns={columns}
        data={data}
        customCellClassNames={[dataSourceTableCellClassNames]}
        autoHeight
        zeroState={
          <Typography fontSize="xs" color={colors.gray[600]}>
            {emptyMessage}
          </Typography>
        }
      />
    </TableContainer>
  );

  const renderSourceGroup = (
    connections: DataSourcesTableItem[],
    uploads: DataSourcesTableItem[],
    emptyMessage: string,
    uploadColumns = fileUploadTableColumns,
  ): React.ReactElement => (
    <>
      {connections.length > 0 && renderSources(connections, '', connectionTableColumns)}
      {uploads.length > 0 && renderSources(uploads, '', uploadColumns)}
      {connections.length === 0 && uploads.length === 0 && renderSources([], emptyMessage)}
    </>
  );

  const ticketingUploadActions: ActionsMenuAction[] = [
    {
      label: 'Upload ticketing files',
      onSelect: (): void => setIsOpenTicketingFileUploadModal(true),
      id: uploadTicketingProviderBtnId,
    },
    ...(shouldShowGithubEmailUploadButton
      ? [{ label: 'Upload GitHub emails', onSelect: (): void => setIsOpenGithubEmailMappingModal(true) }]
      : []),
  ];

  return (
    <RdLockPageLayout
      title={
        <PageTitle>
          Data Sources |{' '}
          <Typography color={colors.blue[600]} fontSize="inherit" fontWeight="inherit">
            Tax Year {fiscalYear.year}
          </Typography>
        </PageTitle>
      }
      subtitle="Import accounting, business component, payroll, tax, timesheet, or ticketing data."
      fullWidth
      pageHeaderBorder={false}
    >
      <GithubEmailMappingSection
        isOpeningUploader={isOpenGithubEmailMappingModal}
        isVisible={canModifyTaxAndTicketingSources && shouldPromptGithubEmailUpload}
        onUpload={(): void => setIsOpenGithubEmailMappingModal(true)}
      />
      <Stack direction="column" fullWidth className="shrink-0">
        <BaseTabs state={tabState}>
          <BaseTab label="Accounting" tabId={TAB_IDS.accounting} />
          <BaseTab label="Business Components" tabId={TAB_IDS.businessComponents} />
          <BaseTab label="Payroll" tabId={TAB_IDS.payroll} />
          <BaseTab label="Tax" tabId={TAB_IDS.tax} />
          <BaseTab label="Ticketing" tabId={TAB_IDS.ticketing} />
          {isSuperUserMode ? <BaseTab label="Timesheets" tabId={TAB_IDS.timesheets} /> : <></>}
        </BaseTabs>
        <TabPanelContainer>
          <BaseTabPanel state={tabState} tabId={TAB_IDS.accounting}>
            <DataSourceSection
              title="Accounting"
              actions={
                canModifyFinancialSources ? (
                  <Button
                    size="sm"
                    variant="primary"
                    rounded="regular"
                    id={connectAccountingBtnId}
                    onClick={openAccountingModal}
                  >
                    Connect Accounting
                  </Button>
                ) : undefined
              }
            >
              {renderSourceGroup(
                tableData.accountingConnections,
                tableData.accountingUploads,
                'No accounting connections or imported data.',
              )}
            </DataSourceSection>
          </BaseTabPanel>

          <BaseTabPanel state={tabState} tabId={TAB_IDS.businessComponents}>
            <DataSourceSection
              title="Business Components"
              actions={
                canModifyTaxAndTicketingSources ? (
                  <Stack direction="column" alignItems="end" gap={1}>
                    <Button
                      size="sm"
                      variant="primary"
                      rounded="regular"
                      id={uploadBusinessComponentsBtnId}
                      disabled={businessComponentsUploadBlockedReason !== undefined}
                      onClick={(): void => setIsOpenBusinessComponentsUploadModal(true)}
                    >
                      Upload Business Components
                    </Button>
                    {businessComponentsUploadBlockedReason ? (
                      <Typography fontSize="xs" color={colors.gray[600]}>
                        {businessComponentsUploadBlockedReason}
                      </Typography>
                    ) : (
                      <></>
                    )}
                  </Stack>
                ) : undefined
              }
            >
              {renderSources(
                businessComponentUploadItems,
                'No business component files uploaded.',
                businessComponentsFileUploadTableColumns,
              )}
            </DataSourceSection>
          </BaseTabPanel>

          <BaseTabPanel state={tabState} tabId={TAB_IDS.payroll}>
            <DataSourceSection
              title="Payroll"
              actions={
                canModifyFinancialSources ? (
                  <Button
                    size="sm"
                    variant="primary"
                    rounded="regular"
                    id={connectPayrollBtnId}
                    onClick={openPayrollModal}
                  >
                    Connect Payroll
                  </Button>
                ) : undefined
              }
            >
              {renderSourceGroup(
                tableData.payrollConnections,
                tableData.payrollUploads,
                'No payroll connections or imported files.',
                payrollFileUploadTableColumns,
              )}
            </DataSourceSection>
          </BaseTabPanel>

          <BaseTabPanel state={tabState} tabId={TAB_IDS.tax}>
            <DataSourceSection
              title="Tax"
              actions={
                canModifyTaxAndTicketingSources ? (
                  <Button
                    size="sm"
                    variant="primary"
                    rounded="regular"
                    id={uploadTaxDocsBtnId}
                    onClick={(): void => setIsOpenPriorYearTaxDocumentModal(true)}
                  >
                    Upload Tax Docs
                  </Button>
                ) : undefined
              }
            >
              {renderSources(tableData.taxUploads, 'No tax documents uploaded.', taxFileUploadTableColumns)}
            </DataSourceSection>
          </BaseTabPanel>

          <BaseTabPanel state={tabState} tabId={TAB_IDS.ticketing}>
            <Stack as="section" aria-label="Ticketing" direction="column" gap={3} fullWidth className="shrink-0">
              <TicketingConnections
                companyId={company.id}
                hasImportedData={tableData.ticketingUploads.length > 0}
                onAddConnection={openTicketingModal}
                readOnly={!canModifyTaxAndTicketingSources}
                slideOver={{ actions: CONNECT_SLIDE_OVER_ACTIONS, onAction: openForConnectionAction }}
                additionalActions={ticketingUploadActions}
              />
              {tableData.ticketingUploads.length > 0 &&
                renderSources(tableData.ticketingUploads, '', ticketingFileUploadTableColumns)}
            </Stack>
          </BaseTabPanel>

          {isSuperUserMode ? (
            <BaseTabPanel state={tabState} tabId={TAB_IDS.timesheets}>
              <DataSourceSection
                title="Timesheets"
                actions={
                  <Button
                    size="sm"
                    variant="primary"
                    rounded="regular"
                    onClick={(): void => setIsOpenOneSchemaTimesheetModal(true)}
                  >
                    Upload Timesheet Files
                  </Button>
                }
              >
                {renderSources(
                  tableData.timesheetUploads,
                  'No timesheet files uploaded.',
                  payrollFileUploadTableColumns,
                )}
              </DataSourceSection>
            </BaseTabPanel>
          ) : (
            <></>
          )}
        </TabPanelContainer>
      </Stack>

      <ConnectProvidersModal
        fiscalYearId={fiscalYear.id}
        {...disabledProviders}
        {...connectModalState}
        isOpen={isConnectOpen}
        initialProvider={initialProvider}
        initialGitHubConnectMethod={initialGitHubConnectMethod}
        initialOrganizationSlug={initialOrganizationSlug}
        close={closeConnectProvidersModal}
      />
      <TicketingFileUploadModal
        close={(): void => setIsOpenTicketingFileUploadModal(false)}
        showOneSchema={({
          oneSchemaJwt,
          permanentUrl,
          signedUrl,
          ticketingEntityType,
          ticketingProvider,
        }: {
          oneSchemaJwt: string;
          permanentUrl: string;
          signedUrl: string;
          ticketingEntityType: TicketingEntityType;
          ticketingProvider: FileUploadTicketingProvider;
        }): void => {
          setOneSchemaParameters({
            oneSchemaJwt,
            permanentUrl,
            signedUrl,
            ticketingEntityType,
            ticketingProvider,
          });
          setIsOpenOneSchemaModal(true);
        }}
        isOpen={isOpenTicketingFileUploadModal}
      />
      <OneSchemaModal
        close={(): void => setIsOpenOneSchemaModal(false)}
        companyId={company.id}
        isOpen={isOpenOneSchemaModal}
        onLaunched={(): void => {
          setIsOpenTicketingFileUploadModal(false);
        }}
        {...oneSchemaParameters}
      />
      <OneSchemaTimesheetModal
        close={(): void => setIsOpenOneSchemaTimesheetModal(false)}
        companyId={company.id}
        isOpen={isOpenOneSchemaTimesheetModal}
      />
      <PriorYearTaxDocumentsModal
        close={(): void => setIsOpenPriorYearTaxDocumentModal(false)}
        isOpen={isOpenPriorYearTaxDocumentModal}
      />
      <RdBusinessComponentsUploadModal
        close={(): void => setIsOpenBusinessComponentsUploadModal(false)}
        companyId={company.id}
        fiscalYearId={fiscalYear.id}
        isOpen={isOpenBusinessComponentsUploadModal}
      />
      <GithubEmailMappingUploadModal
        close={(): void => setIsOpenGithubEmailMappingModal(false)}
        companyId={company.id}
        isOpen={isOpenGithubEmailMappingModal}
      />
      <Modal isOpen={!!itemToDelete} setIsOpen={onCancelToggleModal}>
        {itemToDelete?.itemType === 'ticketing-file' ? (
          <DeleteTicketingFileUpload close={onCancelToggleModal} itemToDelete={itemToDelete} />
        ) : itemToDelete?.itemType === 'tax-document' ? (
          <DeletePriorFiscalYearDocument close={onCancelToggleModal} itemToDelete={itemToDelete} />
        ) : itemToDelete?.itemType === 'rd-business-components-file' ? (
          <DeleteRdBusinessComponentUpload
            close={onCancelToggleModal}
            fiscalYearId={fiscalYear.id}
            itemToDelete={itemToDelete}
          />
        ) : (
          <DeleteFiscalYearProvider
            close={onCancelToggleModal}
            fiscalYearId={fiscalYear.id}
            accountTableItem={itemToDelete}
          />
        )}
      </Modal>
    </RdLockPageLayout>
  );
};
